import React, { useRef, useEffect, useState, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { logError } from "utility/Function";
import { trackEvent } from "utility/mixpanel";
import { UrlSearchParams } from "types/enums";
import { useInViews } from "utility/useInViews";
import { ReactComponent as BookMarkIcon } from "Assets/icon/bookmark.svg";

import { DefaultPlayer as Video } from "react-html5video";
import "react-html5video/dist/styles.css";

import appInfo from "utility/app-info";
import { LEARNER_RECORDING } from "utility/graphQl/query";
import { LearnerRecording } from "apps/user/learner/api/types";
import { usePatternBackground } from "providers/pattern-background";
import useReportRecordingEngagement from "./hooks/use-report-recording-engagement";
import useFavorite from "apps/user/learner/hooks/use-favourite";
import { Button, IconButton } from "components/ui";
import {
  PageLoading,
  PageError,
} from "apps/user/learner/components/page-container";
import DateTimePicker from "apps/user/learner/components/date-time-picker";
import { useRecordingSessionActions } from "apps/user/learner/hooks/use-recording-session-actions";

import { usePage } from "providers/page";
import { useTranslation } from "lib/i18n/use-translation";
import PractitionerCard from "apps/user/learner/components/practitioner-card";
import ReacrdingThumbnailFallbackImage from "Assets/images/reacrdingThumbPH.jpg";
import { ReactComponent as PlayIcon } from "Assets/playIcon.svg";

import styles from "./index.module.scss";
import {
  CategoryPill,
  ContentInfoSection,
} from "apps/user/learner/components/content";
import contentStyles from "apps/user/learner/components/content/styles.module.scss";

interface RecordingDateTimePickerProps {
  recording: LearnerRecording;
}

const RecordingDateTimePicker: React.FC<RecordingDateTimePickerProps> = ({
  recording,
}) => {
  const { t } = useTranslation("components");
  const recordingActions = useRecordingSessionActions({ recording });
  const isDateTimeSelected = !!recording.scheduledCalendarEvent;
  const defaultDateTime = new Date(Date.now() + 60 * 60 * 1000).toISOString();
  return (
    <DateTimePicker
      isDateTimeSelected={isDateTimeSelected}
      selectedDateTime={
        isDateTimeSelected
          ? recording.scheduledCalendarEvent.fromDatetimeUtc
          : undefined
      }
      onDateTimeSelect={(dateTime: string) => {
        recordingActions.SCHEDULE.action(dateTime);
      }}
      onDateTimeDelete={() => {
        recordingActions.DELETE.action();
      }}
      title={t("components:dateTimePicker.calendarTitle")}
      description={t("components:dateTimePicker.calendarDescription")}
      isAddingLoading={recordingActions.SCHEDULE.loading}
      isDeletingLoading={recordingActions.DELETE.loading}
      endDateTime={
        isDateTimeSelected
          ? recording.scheduledCalendarEvent.toDatetimeUtc
          : undefined
      }
      defaultDateTime={defaultDateTime}
    />
  );
};

interface RecordingDetailProps {
  recordingId: string;
}

const RecordingDetail: React.FC<RecordingDetailProps> = ({ recordingId }) => {
  const { t } = useTranslation(["sessions", "common", "feedback"]);
  const navigate = useNavigate();
  const location = useLocation();
  const pageContext = usePage();
  const onPrefixPageTitle = pageContext?.onPrefixPageTitle;
  const [isPatternOverlayRemoved, setIsPatternOverlayRemoved] = useState(false);
  const [searchParams] = useSearchParams();
  const autoplayParam = searchParams.get(UrlSearchParams.Autoplay);
  const isAutoplayEnabled = autoplayParam === "true";
  const videoRef = useRef<HTMLVideoElement>(null);
  const isBeginSessionTriggered = useRef(false);
  enum VideoState {
    NotStarted = "not-started",
    Playing = "playing",
    Paused = "paused",
  }

  const [videoState, setVideoState] = useState<VideoState>(
    VideoState.NotStarted
  );

  const isFromTakingAssessment =
    location.state?.referrer === "/user/woneindex/assessment";

  const [showAfterVideoFeedbackScreen, setShowAfterVideoFeedbackScreen] =
    useState(isFromTakingAssessment || false);
  const isViewSentRef = useRef(false);
  const { onFavourite, loading: isFavouriteLoading } = useFavorite();
  const { onOpen, onPlay, onEnd, onProgress } =
    useReportRecordingEngagement(recordingId);
  const { ref, inView } = useInViews();

  const { loading, error, data } = useQuery(LEARNER_RECORDING, {
    onCompleted() {
      onOpen();
    },
    onError(error) {
      logError("Error while fetching recording detail", error);
    },
    variables: {
      id: recordingId,
      appInfo,
    },
  });
  const { removePatternOverlay } = usePatternBackground();

  const listName = location?.state?.listName;
  const listPosition = location?.state?.listPosition;

  const trackRecordingView = useCallback(() => {
    if (!data?.learnerRecording || isViewSentRef.current) return;

    const { id, title } = data.learnerRecording as LearnerRecording;

    trackEvent("Recording Viewed", {
      "Recording ID": id,
      "Recording Title": title,
      "List Name": listName,
      "List Position": listPosition,
    });

    isViewSentRef.current = true;
  }, [data?.learnerRecording, listName, listPosition]);

  useEffect(() => {
    if (inView && data?.learnerRecording) {
      trackRecordingView();
    }
  }, [inView, data?.learnerRecording, trackRecordingView]);

  useEffect(() => {
    return () => {
      removePatternOverlay(false);
    };
  }, [removePatternOverlay]);
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      const { id, title } = data.learnerRecording as LearnerRecording;

      const listName = location.state?.listName;
      const listPosition = location.state?.listPosition;

      trackEvent(
        isFullscreen
          ? "Recording Video Fullscreen Entered"
          : "Recording Video Fullscreen Exited",
        {
          "Recording ID": id,
          "Recording Title": title,
          "List Name": listName,
          "List Position": listPosition,
        }
      );
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, [
    data?.learnerRecording,
    location.state?.listName,
    location.state?.listPosition,
  ]);

  useEffect(() => {
    if (onPrefixPageTitle && data?.learnerRecording?.title) {
      onPrefixPageTitle(data?.learnerRecording?.title);
    }
  }, [data?.learnerRecording?.title, onPrefixPageTitle]);

  if (loading) {
    return (
      <div className={contentStyles["modal-error-loading-screen"]}>
        <PageLoading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={contentStyles["modal-error-loading-screen"]}>
        <PageError />
      </div>
    );
  }
  const {
    title,
    about,
    videoThumbnailUrl,
    practitioner,
    videoUrl,
    isFavourite,
    topic,
    videoDurationText,
  } = data.learnerRecording;

  const handleRemovePatternOverlay = () => {
    removePatternOverlay(true);
  };

  const handleAfterVideoFeedbackScreen = () => {
    onEnd();
    setIsPatternOverlayRemoved(false);
    setShowAfterVideoFeedbackScreen(true);
  };

  if (showAfterVideoFeedbackScreen) {
    const { id } = data.learnerRecording as LearnerRecording;
    navigate(`/user/recordings/${id}/feedback`, {
      state: {
        sessionId: id,
        type: "RECORDING",
      },
    });
  }

  const handleVideoPlay = () => {
    const previousVideoState = videoState;
    setVideoState(VideoState.Playing);
    if (!isBeginSessionTriggered.current && data?.learnerRecording) {
      const { id, title } = data.learnerRecording as LearnerRecording;

      const listName = location.state?.listName;
      const listPosition = location.state?.listPosition;

      trackEvent(
        previousVideoState === VideoState.NotStarted
          ? "Recording Video Player Begin Button Clicked"
          : "Recording Video Player Resume Button Clicked",
        {
          "Recording ID": id,
          "Recording Title": title,
          "List Name": listName,
          "List Position": listPosition,
        }
      );
    }

    isBeginSessionTriggered.current = false;

    if (!isPatternOverlayRemoved) {
      setIsPatternOverlayRemoved(true);
      handleRemovePatternOverlay();
    }
    onPlay();
    if (previousVideoState === VideoState.NotStarted) {
      setTimeout(() => {
        let videoElement: HTMLVideoElement | null = null;
        videoElement = document.querySelector(
          ".main-video video"
        ) as HTMLVideoElement;
        if (!videoElement) {
          videoElement = document.querySelector("video") as HTMLVideoElement;
        }

        if (videoElement && videoElement.requestFullscreen) {
          videoElement.requestFullscreen().catch((error) => {
            console.error("Error entering fullscreen:", error);
          });
        }
      }, 100);
    }
  };

  const handleVideoPause = () => {
    setVideoState(VideoState.Paused);
    if (!isBeginSessionTriggered.current && data?.learnerRecording) {
      const { id, title } = data.learnerRecording as LearnerRecording;

      const listName = location.state?.listName;
      const listPosition = location.state?.listPosition;

      trackEvent("Recording Video Player Pause Button Clicked", {
        "Recording ID": id,
        "Recording Title": title,
        "List Name": listName,
        "List Position": listPosition,
      });
    }
    isBeginSessionTriggered.current = false;
  };

  const handleButtonClick = () => {
    const VIDEO_STATE_EVENT_MAP = {
      [VideoState.NotStarted]: "Recording CTA Begin Session Button Click",
      [VideoState.Playing]: "Recording CTA Pause Button Click",
      [VideoState.Paused]: "Recording CTA Resume Button Click",
    };
    if (data?.learnerRecording) {
      const { id, title } = data.learnerRecording as LearnerRecording;

      const listName = location.state?.listName;
      const listPosition = location.state?.listPosition;

      trackEvent(VIDEO_STATE_EVENT_MAP[videoState], {
        "Recording ID": id,
        "Recording Title": title,
        "List Name": listName,
        "List Position": listPosition,
      });
    }
    isBeginSessionTriggered.current = true;

    let videoElement: HTMLVideoElement | null = null;
    videoElement = document.querySelector(
      ".main-video video"
    ) as HTMLVideoElement;
    if (!videoElement) {
      videoElement = document.querySelector("video") as HTMLVideoElement;
    }

    if (!videoElement) {
      return;
    }

    if (
      videoState === VideoState.NotStarted ||
      videoState === VideoState.Paused
    ) {
      videoElement.play().catch((error) => {
        console.error("Error playing video:", error);
      });
    } else if (videoState === VideoState.Playing) {
      videoElement.pause();
    }
  };

  const getButtonText = () => {
    switch (videoState) {
      case VideoState.Playing:
        return t("sessions:recording.pause");
      case VideoState.Paused:
        return t("sessions:recording.resume");
      default:
        return t("sessions:recording.beginSession");
    }
  };

  const getButtonIcon = () => {
    switch (videoState) {
      case VideoState.NotStarted:
        return <PlayIcon className={styles["play-button-icon"]} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles["session-container"]} ref={ref}>
      <div className={styles["session-video-wrapper"]}>
        <div className={styles["session-video-placeholder"]}>
          <Video
            className={styles["main-video"]}
            autoPlay={isAutoplayEnabled || isPatternOverlayRemoved}
            muted={false}
            controls={["PlayPause", "Seek", "Time", "Volume", "Fullscreen"]}
            poster={videoThumbnailUrl || ReacrdingThumbnailFallbackImage}
            onPlay={handleVideoPlay}
            onPause={handleVideoPause}
            onEnded={handleAfterVideoFeedbackScreen}
            onTimeUpdate={onProgress}
            ref={videoRef}
          >
            <source src={videoUrl} type="video/mp4" />
          </Video>
        </div>
        {videoState === VideoState.NotStarted && (
          <div
            className={styles["video-poster-overlay"]}
            onClick={handleButtonClick}
          >
            <img
              src={videoThumbnailUrl || ReacrdingThumbnailFallbackImage}
              alt="Video thumbnail"
              className={styles["video-poster-image"]}
            />
            <div className={styles["video-overlay"]}>
              <div className={styles["session-info-headings"]}>
                <div className={styles["video-overlay-duration-container"]}>
                  <p className={styles["video-overlay-duration"]}>
                    {videoDurationText}
                  </p>
                  {topic && <CategoryPill text={topic} />}
                </div>
                <p className={styles["video-overlay-title"]}>{title}</p>
                <p className={styles["video-overlay-practitioner"]}>
                  {t("feedback:withPractitioner", {
                    name: practitioner.fullName,
                  })}
                </p>
              </div>
            </div>
            <div className={styles["video-play-button"]}>
              <PlayIcon className={styles["play-icon"]} />
            </div>
          </div>
        )}
      </div>
      <div className={styles["content-layout"]}>
        <div className={styles["session-info-button-container"]}>
          <div className={styles["session-info-video-button"]}>
            <Button
              variant="enclosed-primary"
              className={styles["session-info-button"]}
              onClick={handleButtonClick}
              icon={getButtonIcon()}
            >
              <span>{getButtonText()}</span>
            </Button>
            <div className={styles["session-favourite-icon"]}>
              <IconButton
                icon={
                  <BookMarkIcon
                    className={
                      isFavourite
                        ? styles["bookmark-filled"]
                        : styles["bookmark-empty"]
                    }
                  />
                }
                shape="square"
                loading={isFavouriteLoading}
                onClick={() => onFavourite(data.learnerRecording)}
                aria-label={
                  isFavourite
                    ? t("common:a11y.removeFromFavourites")
                    : t("common:a11y.addToFavourites")
                }
                className={styles["favourite-button"]}
                variant="enclosed-primary"
                monochromeColour="white"
              />
            </div>
          </div>
          <RecordingDateTimePicker recording={data.learnerRecording} />
        </div>
        <ContentInfoSection
          title={t("sessions:detail.inThisSession")}
          content={about}
        />
        <PractitionerCard practitioner={practitioner} />
      </div>
    </div>
  );
};

export default RecordingDetail;
